// AnalyticsDashboard — admin /reports/analytics (CHG-1659).
//
// Spec authority: specs/tier-05-experience/analytics-dashboard.entity-hint.yaml
// (pages.dashboard_v2 block). Documented hand-roll exception per DRIFT-702.
// Mirrors Overview.tsx (CHG-1238) precedent for hand-rolled DashboardPage
// surfaces.

import { useQuery } from "@tanstack/react-query";
import { DashboardPage } from "../components/composition/DashboardPage";
import {
  StatStrip,
  type StatStripItem,
} from "../components/composition/StatStrip";
import { reportsApi, type AnalyticsTimeline } from "../api/reports";

function statsFromTimeline(data: AnalyticsTimeline | undefined): StatStripItem[] {
  const totalMinorUnits = data?.total_minor_units ?? null;
  const totalCount = data?.total_count ?? null;
  const avgMinorUnits =
    totalMinorUnits != null && totalCount && totalCount > 0
      ? Math.round(totalMinorUnits / totalCount)
      : null;
  return [
    {
      id: "timeline_total",
      label: "Total raised (30d)",
      value: totalMinorUnits,
      format: "currency",
      currency: "USD",
      tone: "g-giving",
    },
    {
      id: "timeline_count",
      label: "Donations (30d)",
      value: totalCount,
      format: "number",
      tone: "g-giving",
    },
    {
      id: "avg_gift",
      label: "Avg gift",
      value: avgMinorUnits,
      format: "currency",
      currency: "USD",
      tone: "g-giving",
    },
  ];
}

function TimelineFeed({ data }: { data: AnalyticsTimeline | undefined }) {
  const buckets = data?.buckets ?? [];
  if (buckets.length === 0) {
    return (
      <section
        className="card"
        aria-labelledby="analytics-timeline-title"
        style={{ padding: 16 }}
      >
        <h2
          id="analytics-timeline-title"
          style={{
            fontSize: 12,
            fontWeight: 500,
            margin: 0,
            color: "var(--color-text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Donation timeline (30 days)
        </h2>
        <p
          style={{
            fontSize: 13,
            color: "var(--color-text-muted)",
            margin: "12px 0 0",
          }}
        >
          No donations recorded in the last 30 days.
        </p>
      </section>
    );
  }
  // Lightweight bar visualization — full chart primitive deferred.
  // CHG-1659 ships value text + relative bars; a richer chart can replace
  // this slot once the chart composition primitive lands.
  const maxMinorUnits = buckets.reduce((m, b) => Math.max(m, b.total_minor_units), 0);
  return (
    <section
      className="card"
      aria-labelledby="analytics-timeline-title"
      style={{ padding: 16 }}
    >
      <h2
        id="analytics-timeline-title"
        style={{
          fontSize: 12,
          fontWeight: 500,
          margin: "0 0 12px",
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        Donation timeline (30 days)
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          gap: "4px 12px",
          alignItems: "center",
          fontSize: 12,
        }}
      >
        {buckets.map((b) => {
          const pct = maxMinorUnits > 0 ? (b.total_minor_units / maxMinorUnits) * 100 : 0;
          return (
            <div key={b.date} style={{ display: "contents" }}>
              <span style={{ color: "var(--color-text-muted)" }}>{b.date}</span>
              <div
                aria-hidden="true"
                style={{
                  height: 6,
                  background: "var(--color-border)",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: "var(--accent, #0066cc)",
                  }}
                />
              </div>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                ${(b.total_minor_units / 100).toFixed(0)}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function AnalyticsDashboard() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["analytics", "timeline", "30d"] as const,
    queryFn: () => reportsApi.fetchAnalyticsTimeline("30d"),
  });
  return (
    <DashboardPage
      title="Analytics dashboard"
      subtitle="Real-time KPIs across donations, donors, programs, and campaigns."
      stats={<StatStrip items={statsFromTimeline(data)} loading={isLoading} />}
      feed={<TimelineFeed data={data} />}
      callout={null}
      loading={isLoading}
      error={error as Error | null}
      onRetry={() => refetch()}
    />
  );
}
